import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getAdminClientsGetAdminClientsQueryKey,
  useAdminClientsPostAdminClients,
} from '@/lib/api/admin-clients/admin-clients';
import type { CreateClientResult } from '@/lib/api/schemas';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Schema = z.object({
  name: z.string().min(1, 'Name is required.').max(200),
  rateLimitPerMin: z.coerce.number().int().positive().max(10_000),
  clientId: z.string().max(64).optional().or(z.literal('')),
  allowedIpCidrs: z.string().max(400).optional().or(z.literal('')),
  adyenBalancePlatform: z.string().max(256).optional().or(z.literal('')),
  adyenScopePlatform: z.string().max(256).optional().or(z.literal('')),
  adyenApiKey: z.string().max(2048).optional().or(z.literal('')),
});
type Form = z.infer<typeof Schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateClientDialog({ open, onOpenChange }: Props) {
  const queryClient = useQueryClient();
  const [created, setCreated] = useState<CreateClientResult | null>(null);
  const [savedAcknowledged, setSavedAcknowledged] = useState(false);
  const [secretVisible, setSecretVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const mutation = useAdminClientsPostAdminClients({
    mutation: {
      onSuccess: async (result) => {
        setCreated(result);
        await queryClient.invalidateQueries({ queryKey: getAdminClientsGetAdminClientsQueryKey() });
      },
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(Schema),
    defaultValues: {
      name: '',
      rateLimitPerMin: 120,
      clientId: '',
      allowedIpCidrs: '',
      adyenBalancePlatform: '',
      adyenScopePlatform: '',
      adyenApiKey: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    const cidrs = values.allowedIpCidrs
      ?.split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      await mutation.mutateAsync({
        data: {
          name: values.name,
          rateLimitPerMin: values.rateLimitPerMin,
          clientId: values.clientId ? values.clientId : null,
          allowedIpCidrs: cidrs && cidrs.length > 0 ? cidrs : null,
          adyenBalancePlatform: values.adyenBalancePlatform || null,
          adyenScopePlatform: values.adyenScopePlatform || null,
          adyenApiKey: values.adyenApiKey || null,
        },
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      toast.error(status === 409 ? 'A client with that identifier already exists.' : 'Could not create client.');
    }
  });

  const close = () => {
    setCreated(null);
    setSavedAcknowledged(false);
    setSecretVisible(false);
    setCopied(false);
    reset();
    onOpenChange(false);
  };

  const copySecret = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.plaintextSecret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Block closing while the secret is still displayed and unacknowledged.
        if (!next && created && !savedAcknowledged) return;
        if (!next) close();
        else onOpenChange(next);
      }}
    >
      <DialogContent>
        {!created ? (
          <>
            <DialogHeader>
              <DialogTitle>Create client</DialogTitle>
              <DialogDescription>
                Adds a partner integration. Generated secret will be shown ONCE after creation.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" {...register('name')} aria-invalid={!!errors.name} />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="rateLimitPerMin">Rate limit (requests / minute)</Label>
                <Input id="rateLimitPerMin" type="number" min={1} {...register('rateLimitPerMin')} />
                {errors.rateLimitPerMin && (
                  <p className="text-xs text-destructive">{errors.rateLimitPerMin.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="clientId">Client ID (optional — server-generated if empty)</Label>
                <Input id="clientId" placeholder="acme-prod" {...register('clientId')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="allowedIpCidrs">Allowed IP CIDRs (comma-separated, optional)</Label>
                <Input id="allowedIpCidrs" placeholder="10.0.0.0/8, 192.168.1.0/24" {...register('allowedIpCidrs')} />
              </div>

              <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
                <div className="text-xs font-medium text-muted-foreground">
                  Adyen credentials (optional — leave blank to route via global config)
                </div>
                <div className="space-y-2">
                  <Label htmlFor="adyenBalancePlatform">Balance Platform</Label>
                  <Input
                    id="adyenBalancePlatform"
                    placeholder="AQE..."
                    autoComplete="off"
                    {...register('adyenBalancePlatform')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="adyenScopePlatform">Scope Platform</Label>
                  <Input
                    id="adyenScopePlatform"
                    placeholder="AQE..."
                    autoComplete="off"
                    {...register('adyenScopePlatform')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="adyenApiKey">API Key</Label>
                  <Input
                    id="adyenApiKey"
                    type="password"
                    placeholder="AQEm..."
                    autoComplete="off"
                    {...register('adyenApiKey')}
                  />
                  <p className="text-xs text-muted-foreground">
                    Encrypted at rest. Only the last 4 characters shown on detail after save.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={close}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting || mutation.isPending}>
                  {mutation.isPending ? 'Creating…' : 'Create client'}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Save this secret — it won&apos;t be shown again</DialogTitle>
              <DialogDescription>
                This is the only time the plaintext secret will be displayed. Rotating produces a new one.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1 rounded-md border border-border bg-muted p-3">
                <div className="flex items-center justify-between gap-2">
                  <code className="break-all font-mono text-xs">
                    {secretVisible ? created.plaintextSecret : '•'.repeat(created.plaintextSecret.length)}
                  </code>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label={secretVisible ? 'Hide secret' : 'Show secret'}
                      onClick={() => setSecretVisible((v) => !v)}
                    >
                      {secretVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label="Copy secret" onClick={copySecret}>
                      {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Client ID: <code className="rounded bg-muted px-1 py-0.5 font-mono">{created.client.clientId}</code>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={savedAcknowledged}
                  onChange={(e) => setSavedAcknowledged(e.target.checked)}
                />
                I&apos;ve stored the secret in a secure vault.
              </label>
            </div>
            <DialogFooter>
              <Button type="button" onClick={close} disabled={!savedAcknowledged}>
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
